import { Component, OnInit, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { MasterService } from "../../services/master.service";
import { StoreService } from "../../services/store.service";
import { MessageService } from "../../services/message.service";
import * as languageLibrary from '../../services/language'
import { FormControl, FormGroup, FormBuilder, Validators } from "@angular/forms";
import { SEARCH_DEBOUNCE_MILLIS, MIN_SEARCH_CHARS, SCROLL_BOTTOM_OUT } from "src/app/constants/list";
import { SecurityService } from "src/app/services/security.service";
import { User } from '../../models/user.model'
import { Invite } from '../../models/invite.model'
import { Subscription } from 'rxjs';

@Component({
    selector: "app-users",
    templateUrl: "./users.component.html",
    styleUrls: ["./users.component.scss"],
    standalone: false
})
export class UsersComponent implements OnInit, OnDestroy {
    /*
    * Variables
    */

    private FILTER_INQUIRY_KEY = 'flt-users-q'

    public information = new FormGroup({
        id: new FormControl<number | null>(null, [Validators.required]),
        contactId: new FormControl<number | null>(null, [Validators.required]),
        firstName: new FormControl<string | null>(null, [Validators.required]),
        lastName: new FormControl<string | null>(null, [Validators.required]),
        email: new FormControl<string | null>(null, [Validators.email, Validators.required])
    })

    // save the language
    public language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    public words = languageLibrary.language

    //  save all users  filtered
    public users: User[] = [];

    // New User invitations
    public usersInvites: Invite[] = [];

    // define if is loading
    public loading: boolean = false;

    // Current page the user is viewing (last loaded).
    public page = 1

    // The number contacts currently being displayed.
    public displayed = 0

    // Indicates whether or not there are no more records to load.
    public exhausted = false

    // Total number of records available
    public records: number

    // The current search text to filter the list by.
    private inquiry: string | null = null

    // The search form group
    public form: FormGroup | null = null

    // define if is sending
    public sending: boolean = false;

    // A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> = null

    // Keep track of how the user wants to sort list.
    private sortables: Object = { 'name': 'ASC' }

    // save the invities success and failed
    public invitations = { success: [], failed: [] };

    private subscriptions: Subscription[] = []

    public urlLink = null

    public userSelected: User = new User()
    /*
    * Lifecycle
    */

    constructor(private master: MasterService, private ms: MessageService, public store: StoreService, private router: Router, public security: SecurityService, private fb: FormBuilder) { }

    ngOnInit() {
        this.form = this.fb.group({
            inquiry: new FormControl<string>('', [])
        })

        this.readFilters()

        this.subscriptions.push(
            this.form.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }

                if (this.inquiry === inquiry) {
                    return
                }

                this.watcher = setTimeout(() => {
                    this.writeFilters()
                    this.inquiry = inquiry
                    this.reset()
                    this.fetch()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )

        this.reset()
        this.fetch()
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /*
    * Functions
    */

    /**
     * Reset the pagination parameters and scroll to the top
    */
    private reset() {
        this.page = 1
        this.exhausted = false
        this.displayed = 0

        const list = document.querySelector('#listing')
        if (!list) {
            return
        }

        list.scrollTo({
            top: 0,
            left: 0,
            behavior: 'auto'
        })
    }

    public sortedAsc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'ASC')
    }

    public sortedDesc = (field: string): Boolean => {
        if (!field || (field.length <= 0) || !this.sortables) {
            return false
        }

        if (!this.sortables[field]) {
            return false
        }

        return (this.sortables[field].toUpperCase() === 'DESC')
    }

    // get users data
    public fetch = async () => {
        if (this.security.notPermitted('users', 'user list', 'view')) {
            return
        }

        if (this.loading) { // already getting next page
            return
        }

        if (this.exhausted) { // currently showing all available records?
            return
        }

        let inquiryParam = ''
        if (this.inquiry && (this.inquiry.length > 0)) {
            inquiryParam = `&q=${encodeURIComponent(this.inquiry)}`
        }

        let sortParam = ''
        if (this.sortables && (Object.keys(this.sortables).length > 0)) {
            const sorts = []
            for (const key of Object.keys(this.sortables)) {
                let direction = ''
                if (this.sortables[key] && this.sortables[key].length > 0) {
                    direction = `|${this.sortables[key]}`
                }

                sorts.push(`${key}${direction}`)
            }

            sortParam = `&sort=${sorts.join(',')}`
        }

        this.loading = true
        await this.master.getAsync(`users?page=${this.page}${inquiryParam}${sortParam}`).then(res => {
            this.loading = false

            if (!res || !res.data) {
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error ? res.data.error : this.words[this.language]['apiNoResponse'] });
                return
            }

            this.records = res.data.records
            this.displayed = Math.min(this.records, (res.data.page * res.data.ipp))
            this.exhausted = (res.data.next === null)

            if (this.page > 1) {
                this.users = this.users.concat(res.data.users)
                return
            }

            this.users = res.data.users
        })
    };

    //  select a user
    public selectUser = (user: User): void => {
        this.userSelected = user
        this.setInformation(user)
    }

    //  save change of user email (single)
    public updateEmail = (emailInputName, id): void => {
        // test email valid
        this.loading = true;
        let email = document.getElementById(emailInputName) as HTMLInputElement;
        if (this.userSelected.email == email.value) {
            this.copyLink(this.userSelected.email, id, "copy_link");
            (document.getElementById("emailHelp") as HTMLInputElement).innerText = '';
            this.reset()
            this.fetch()
            return
        }

        // if email isn´t equal
        if (this.information.get('email').invalid) {
            (document.getElementById("emailHelp") as HTMLInputElement).innerText = "Invalid Email Address";
            this.loading = false;
            return
        }

        // verufy if email exist
        this.emailInUse(email.value, (used) => {
            if (used) {
                this.ms.sendMessage("alert", { type: "danger", text: used.error });
                this.loading = false;
                return
            }

            // change email
            this.master.post("updateUser", { id: id, email: email.value }, (res) => {
                this.loading = false;

                if (!res || !res.data) {
                    this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'], });
                    return
                }

                if (res.status !== 200) {
                    this.ms.sendMessage("alert", { type: "danger", text: res.data.error, });
                    return
                }

                this.userSelected.email = email.value;
                (document.getElementById("emailHelp") as HTMLInputElement).innerText = ""
                this.copyLink(email.value, id, "copy_link");
                this.reset()
                this.fetch()
            }
            )
        })
    };

    /**
     * 
     */
    public sendInvitation = (): void => {
        this.sending = true;
        if (this.information.get('email').invalid) {
            this.sending = false;

            const help = (document.getElementById("emailHelp") as HTMLInputElement)
            if (help) {
                help.innerText = 'Invalid Email Address';
            }

            return
        }

        const id = this.information.get('id').value
        const email = this.information.get('email').value

        const payload = {
            firstName: this.information.get('firstName').value,
            lastName: this.information.get('lastName').value,
            email: email
        }

        if (this.userSelected && (this.userSelected.email == email)) {
            const help = (document.getElementById("emailHelp") as HTMLInputElement)
            if (help) {
                help.innerText = '';
            }

            this.sendEmail(this.information.get('email').value);

            if (this.information.dirty) {
                if (!id || !isNaN(id) || (id <= 0)) {
                    this.insert(payload)
                    return
                }

                this.modify(id, payload)
            }

            return
        }

        // verify if email exist
        this.emailInUse(email, (used) => {
            if (used) {
                this.ms.sendMessage("alert", { type: "danger", text: used.error });
                this.loading = false;
                this.sending = false;
                return
            }

            if (!id || !isNaN(id) || (id <= 0)) {
                this.insert(payload)
                return
            }

            this.modify(id, payload)
        })
    }

    /**
     * Add a new user to the system.
     * @param payload A single user meta data object
     */
    private insert = (payload) => {
        this.master.post(`users`, { users: [payload] }, (res) => {
            this.loading = false;
            this.sending = false;

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'], });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error, });
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['success'] });

            this.information.reset()
            this.reset()
            this.fetch()

            const users = res.data.users
            if (!users || !users.success || (users.success.length <= 0)) {
                return
            }

            this.urlLink = users.success[0].link
        })
    }

    /**
     * Modify an existing user in the system.
     * @param id Unique internal identity of the user being modified.
     * @param payload A single user meta data object.
     */
    private modify = (id, payload) => {
        this.master.put(`users/${id}`, payload, (res) => {
            this.loading = false;
            this.sending = false;

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'], });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error, });
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['success'] });

            if (res.data.link) {
                this.urlLink = res.data.link
            }

            this.information.reset()
            this.reset()
            this.fetch()
        })
    }

    // send the email
    public sendEmail = (email): void => {
        this.sending = true;
        this.master.post("reset", { email: email }, (res) => {
            this.sending = false;

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['invitationsWasSend'] });
        })
    };

    // copy link
    public copyLink = (email, id, inputName): void => {
        this.loading = true;
        this.master.post("tokenLink", { email: email, id: id, }, (res) => {
            this.loading = false;

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['copySuccess'] });

            const input = document.getElementById(inputName) as HTMLInputElement;
            input.value = "";
            input.value = res.data.link;

            if (navigator && navigator.clipboard) {
                navigator.clipboard.writeText(res.data.link)
            }
        })
    }

    /**
     * Check to see if the given email is already being used.
     * @param email An email to check and make sure it is safe to use.
     * @param callback Notification with the result of the check.
     */
    public emailInUse = (email, callback) => {
        if (!email) {
            callback(false)
            return
        }

        this.master.get(`users?ipp=1&email=${encodeURIComponent(email)}`, (res) => {
            if (!res || !res.data || (res.status !== 200)) {
                callback(true)
                return
            }

            const matches = res.data.users
            if (!matches || (matches.length <= 0)) {
                callback(false)
                return
            }

            callback(true)
        })
    };

    // todo: listen to output of navbar
    public listenerNav = (e): void => {
        switch (e.message) {
            case 'changeLanguage':
                this.language = e.value
                break
            case 'setPermissions':
                this.reset()
                this.fetch()
                break
            default:
                break
        }
    }

    /**
     * Handle scroll events of the list container and load 
     * the next page when avilable and the user scrolled 
     * to the bottom.
     * @param e The scroll event that trigggered this handler.
     */
    public scrolled = (e): void => {
        if (!e || !e.target) {
            return
        }

        if (this.exhausted) {
            return
        }

        if (this.loading) {
            return
        }

        const location = Math.abs(e.target.scrollHeight - e.target.clientHeight - e.target.scrollTop)

        if (location >= SCROLL_BOTTOM_OUT) {
            return
        }

        this.page = this.page + 1
        this.fetch()
    }

    /**
     * 
     * @param field The field that user wants to sort by. 
     */
    public sort = (field: string): void => {
        if (!field || (field.length <= 0)) {
            return
        }

        if (this.sortables[field]) { // need to toggle the direction?
            switch (this.sortables[field]) {
                case 'ASC':
                    this.sortables[field] = 'DESC'
                    break
                default:
                    this.sortables[field] = 'ASC'
                    break
            }

            this.reset()
            this.fetch()
            return
        }

        this.sortables = {}
        this.sortables[field] = 'ASC'
        this.reset()
        this.fetch()
    }

    public setInformation(user) {
        this.urlLink = null
        this.information.setValue({
            id: parseInt(user.id),
            contactId: parseInt(user.contactId),
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
        })
    }


    public add = () => {
        this.information.reset()
        this.urlLink = null
    }

    public async copyInviteURL() {
        if (!this.urlLink || (this.urlLink.length <= 0)) {
            return
        }

        try {
            await navigator.clipboard.writeText(this.urlLink)
            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['copySuccess'] })
        }
        catch (error) { }
    }

    public deactivateUser() {
        if (this.information.invalid) {
            return
        }

        this.master.discard(`users/${this.information.get('id').value}`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }

            this.information.reset()
            this.reset()

            this.users = res.data.users;
        })
    }


    public reactivateUser() {
        if (this.information.invalid) {
            return
        }

        this.master.patch(`users/${this.information.get('id').value}/reactivate`, null, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: 'An unknown error has occurred.' })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "warning", text: res.data.error })
                return
            }
            this.information.reset()
            this.reset()

            this.users = res.data.users;
        })
    }

    /**
     * 
     * @param name 
     * @returns The type (color) of badge to show for the status
     */
    public status = (name) => {
        if (!name) {
            return 'light'
        }

        switch (name.toLowerCase()) {
            case 'active':
                return 'success'
            case 'inactive':
                return 'danger'
            default:
                return 'secondary'
        }
    }

    /**
     * Check session storage for stored filters and apply them if found.
     */
    private readFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = sessionStorage.getItem(this.FILTER_INQUIRY_KEY)
        if (qFilter && (qFilter.length > 0)) {
            this.form.get('inquiry').setValue(qFilter, { emitEvent: false })
            this.inquiry = qFilter
        }
    }

    /**
     * Save the existing list fitlers to session storage for later retrieval.
     */
    private writeFilters = () => {
        if (!sessionStorage) {
            return
        }

        const qFilter = this.form.get('inquiry').value
        if (!qFilter || (qFilter.length <= 0)) {
            sessionStorage.removeItem(this.FILTER_INQUIRY_KEY)
        }
        else {
            sessionStorage.setItem(this.FILTER_INQUIRY_KEY, qFilter)
        }
    }
}
